import { toast } from 'react-toastify';

import { getNotificationContent } from 'components';
import { SCAN_NAMES } from 'consts';
import { store } from 'store';
import { bn } from 'utils/BigNumber';
import { getScanUrl } from 'utils/helpers';
import { handleWeb3Error } from 'utils/helpers/handleError';
import { usePublicClient, useWriteContract } from 'wagmi';

import { useTransactionExpired } from './useTransactionExpired';

const wethAbi = [
  {
    name: 'deposit',
    type: 'function',
    stateMutability: 'payable',
    inputs: [],
    outputs: [],
  },
  {
    name: 'withdraw',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'wad', type: 'uint256' }],
    outputs: [],
  },
];

export const useWrapUnwrap = () => {
  const { chainId, nativeToken, wrappedNativeToken, balances } = store.web3Store;
  const { tokenIn, tokenOut, value, setValue } = store.swapStore;

  const { writeContractAsync } = useWriteContract();

  const publicClient = usePublicClient({ chainId });
  const { setExpiredTimer, clearExpiredTimer } = useTransactionExpired();

  const wrap = async (amount: string) => {
    return writeContractAsync({
      address: wrappedNativeToken.address,
      abi: wethAbi,
      functionName: 'deposit',
      value: BigInt(amount),
    });
  };

  const unwrap = async (amount: string) => {
    return writeContractAsync({
      address: wrappedNativeToken.address,
      abi: wethAbi,
      functionName: 'withdraw',
      args: [amount],
    });
  };

  const wrapUnwrap = async () => {
    if (!publicClient || !tokenIn || !tokenOut) return;

    try {
      setExpiredTimer();
      const gasPrice = await publicClient.getGasPrice();
      const gasLimit = 50_000;
      const gas = bn(gasLimit).mul(gasPrice.toString());

      const isWrap = tokenIn?.address === nativeToken.address;
      let amount: ReturnType<typeof bn>;

      if (isWrap) {
        const balance = balances[nativeToken.address] || '0';
        amount = bn.minimum(bn(balance).minus(gas), bn(value).toDecimals(nativeToken.decimals));

        if (amount.lte(0)) {
          toast.warn(
            `Not enough funds to complete the transaction. Please leave some ${nativeToken.symbol} for gas fees.`
          );
          return;
        }
      } else {
        amount = bn(value).toDecimals(wrappedNativeToken.decimals);
      }

      const hash = await (isWrap ? wrap : unwrap)(amount.toString());
      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      const scanLink = getScanUrl(`/tx/${receipt.transactionHash}`, chainId);

      const successMessage = `You swapped ${bn(amount).toView(tokenIn.decimals)} ${tokenIn.symbol} to ${tokenOut.symbol}`;

      if (receipt.status === 'success') {
        toast.success(
          getNotificationContent({
            title: 'Transaction Succeeded!',
            text: successMessage,
            href: scanLink,
            scanName: SCAN_NAMES[chainId],
          })
        );
        setValue('');
      }

      if (receipt.status === 'reverted') {
        toast.error(
          getNotificationContent({
            isError: true,
            title: 'Failed to swap',
            href: scanLink,
            scanName: SCAN_NAMES[chainId],
          })
        );
      }
    } catch (err) {
      handleWeb3Error({ err, source: 'swap' });
    }

    clearExpiredTimer();
  };

  return wrapUnwrap;
};
